import React from 'react';
import { DollarSign, Calendar, Percent, Shield, Wrench, Package } from 'lucide-react';
import type { ValidationErrors } from '../utils/validation';

interface FormFieldsProps {
  leaseData: {
    msrp: string;
    sellingPrice: string;
    residualValue: string;
    moneyFactor: string;
    term: string;
    downPayment: string;
    taxes: string;
    fees: string;
    dealerAddOns: {
      protectionPackage: string;
      serviceContract: string;
      dealerInstalledEquipment: string;
      predeliveryCharge: string;
    };
    acquisitionFee: string;
  };
  validationErrors: ValidationErrors;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function FormFields({ leaseData, validationErrors, handleInputChange }: FormFieldsProps) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" title="Manufacturer's Suggested Retail Price - The official price set by the manufacturer">
            MSRP
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="msrp"
              value={leaseData.msrp}
              onChange={handleInputChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="35000"
              title="The sticker price of the vehicle before any negotiations or discounts"
            />
          </div>
          {validationErrors.msrp && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.msrp}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" title="The final negotiated price of the vehicle">
            Selling Price
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="sellingPrice"
              value={leaseData.sellingPrice}
              onChange={handleInputChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="32000"
              title="The actual price you'll pay for the vehicle after negotiations - aim for 5-10% below MSRP"
            />
          </div>
          {validationErrors.sellingPrice && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.sellingPrice}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" title="The estimated value of the vehicle at the end of the lease">
            Residual Value
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="residualValue"
              value={leaseData.residualValue}
              onChange={handleInputChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="20000"
              title="The predicted value of the vehicle at lease end - higher residual values mean lower monthly payments"
            />
          </div>
          {validationErrors.residualValue && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.residualValue}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" title="The interest rate factor used to calculate lease charges">
            Money Factor
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Percent className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="moneyFactor"
              value={leaseData.moneyFactor}
              onChange={handleInputChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="0.00250"
              title="Multiply by 2400 to get the APR - aim for 0.00200 or lower for competitive rates"
            />
          </div>
          {validationErrors.moneyFactor && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.moneyFactor}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" title="Length of the lease in months">
            Term (months)
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="term"
              value={leaseData.term}
              onChange={handleInputChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="36"
              title="Most leases are 24, 36, or 39 months - longer terms mean more depreciation risk"
            />
          </div>
          {validationErrors.term && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.term}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" title="Initial payment made at lease signing">
            Down Payment
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="downPayment"
              value={leaseData.downPayment}
              onChange={handleInputChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="3000"
              title="Keep down payments low on leases - you won't recover this if the car is totaled"
            />
          </div>
          {validationErrors.downPayment && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.downPayment}</p>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center" title="Additional products and services offered by the dealer">
          <Package className="w-4 h-4 mr-1 text-blue-500" />
          Dealer Add-ons
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" title="Paint protection, fabric guard, etc. - often high-margin items">
              Protection Package
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Shield className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="dealerAddOns.protectionPackage"
                value={leaseData.dealerAddOns.protectionPackage}
                onChange={handleInputChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="0"
                title="Optional protection packages - consider negotiating these or declining if not valuable to you"
              />
            </div>
            {validationErrors.dealerAddOns?.protectionPackage && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.dealerAddOns.protectionPackage}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" title="Extended warranty or maintenance plans">
              Service Contract
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Wrench className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="dealerAddOns.serviceContract"
                value={leaseData.dealerAddOns.serviceContract}
                onChange={handleInputChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="0"
                title="Extended service plans - may not be necessary for a lease covered by manufacturer warranty"
              />
            </div>
            {validationErrors.dealerAddOns?.serviceContract && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.dealerAddOns.serviceContract}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" title="Additional equipment installed by the dealer">
              Dealer Installed Equipment
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Package className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="dealerAddOns.dealerInstalledEquipment"
                value={leaseData.dealerAddOns.dealerInstalledEquipment}
                onChange={handleInputChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="0"
                title="Additional equipment added by dealer - verify if these items are truly necessary"
              />
            </div>
            {validationErrors.dealerAddOns?.dealerInstalledEquipment && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.dealerAddOns.dealerInstalledEquipment}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" title="Preparation and delivery charges">
              Pre-delivery Charge
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="dealerAddOns.predeliveryCharge"
                value={leaseData.dealerAddOns.predeliveryCharge}
                onChange={handleInputChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="0"
                title="Charges for vehicle preparation - some dealers may be willing to reduce or waive this fee"
              />
            </div>
            {validationErrors.dealerAddOns?.predeliveryCharge && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.dealerAddOns.predeliveryCharge}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}